// Foot-stepping rule. Each foot has a "rest" position (where it wants to
// be relative to the body, in world space) and a "current" position (where
// it actually is). When current drifts past a threshold from rest — and
// the diagonal-opposite leg is grounded — the foot lifts and arcs to a
// new rest, leaning into body velocity via a lookahead.
//
// Pure logic. No React, no Three. The caller computes world-space rest
// from the body transform and foot offset each frame, and passes the
// "diagonal grounded" flag based on the matching foot's stepper state.
//
// Reference: t3ssel8r's "How I Made My Game's Procedural Animation,"
// Codrin Pavel's spider-leg tutorial.

import type { Vec3 } from './ik';

export interface FootStepperState {
  /** World-space position of the foot right now. */
  current: Vec3;
  /** True when mid-step (interpolating arcStart → arcEnd). */
  lifting: boolean;
  /** Step progress 0..1 while lifting. */
  t: number;
  /** Start of the current arc (world-space). */
  arcStart: Vec3;
  /** Target of the current arc (world-space). */
  arcEnd: Vec3;
}

export interface FootStepperParams {
  /** Trigger a step when ||current - rest|| > this. */
  stepThreshold: number;
  /** How long a step takes, in seconds. */
  stepDuration: number;
  /** End position is rest + bodyVel * lookahead — leans into motion. */
  lookahead: number;
  /** Peak vertical lift during the arc, world units. */
  arcHeight: number;
}

export function makeFootStepperState(at: Vec3): FootStepperState {
  return {
    current: at,
    lifting: false,
    t: 0,
    arcStart: at,
    arcEnd: at,
  };
}

export function updateFootStepper(
  state: FootStepperState,
  restWorld: Vec3,
  bodyVel: Vec3,
  dt: number,
  params: FootStepperParams,
  diagonalGrounded: boolean,
): void {
  if (state.lifting) {
    state.t = Math.min(1, state.t + dt / params.stepDuration);

    if (state.t >= 1) {
      state.lifting = false;
      state.current = state.arcEnd;
      return;
    }

    const ease = easeInOut(state.t);
    // Vertical lift peaks at t=0.5; sin(πt) gives a smooth 0→1→0 curve.
    const lift = Math.sin(state.t * Math.PI) * params.arcHeight;
    state.current = [
      state.arcStart[0] * (1 - ease) + state.arcEnd[0] * ease,
      state.arcStart[1] * (1 - ease) + state.arcEnd[1] * ease + lift,
      state.arcStart[2] * (1 - ease) + state.arcEnd[2] * ease,
    ];
    return;
  }

  if (!diagonalGrounded) return;

  const dx = state.current[0] - restWorld[0];
  const dy = state.current[1] - restWorld[1];
  const dz = state.current[2] - restWorld[2];
  const distSq = dx * dx + dy * dy + dz * dz;
  if (distSq <= params.stepThreshold * params.stepThreshold) return;

  state.lifting = true;
  state.t = 0;
  state.arcStart = state.current;
  state.arcEnd = [
    restWorld[0] + bodyVel[0] * params.lookahead,
    restWorld[1] + bodyVel[1] * params.lookahead,
    restWorld[2] + bodyVel[2] * params.lookahead,
  ];
}

function easeInOut(t: number): number {
  return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
}
